/** Max PDF pages per resolve request — stays under ~60s gateway limits. */
export const PDF_RESOLVE_CHUNK_SIZE = 10;

function bytesToLatin1(arrayBuffer) {
  const bytes = arrayBuffer instanceof Uint8Array
    ? arrayBuffer
    : new Uint8Array(arrayBuffer);
  let out = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    out += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return out;
}

/**
 * Best-effort page count from raw PDF bytes (no pdf.js dependency).
 * Works for ReportLab sticker PDFs and most simple page trees.
 */
export function estimatePdfPageCount(arrayBuffer) {
  if (!arrayBuffer) return 1;
  const text = bytesToLatin1(arrayBuffer);
  let maxCount = 0;
  for (const match of text.matchAll(/\/Type\s*\/Pages\b[^]*?\/Count\s+(\d+)/g)) {
    const n = Number(match[1]);
    if (Number.isFinite(n) && n > maxCount) maxCount = n;
  }
  if (maxCount > 0) return maxCount;

  const pageObjects = text.match(/\/Type\s*\/Page(?!\s*s)\b/g);
  if (pageObjects?.length) return pageObjects.length;

  const bareCounts = [...text.matchAll(/\/Count\s+(\d+)/g)].map((m) => Number(m[1]));
  const positive = bareCounts.filter((n) => Number.isFinite(n) && n > 0);
  if (positive.length) return Math.max(...positive);

  return 1;
}

export function buildPdfPageChunks(pageCount, chunkSize = PDF_RESOLVE_CHUNK_SIZE) {
  const total = Math.max(1, Number(pageCount) || 1);
  const size = Math.max(1, Number(chunkSize) || PDF_RESOLVE_CHUNK_SIZE);
  if (total <= size) {
    return [{ start: 1, end: total }];
  }
  const chunks = [];
  for (let start = 1; start <= total; start += size) {
    chunks.push({ start, end: Math.min(total, start + size - 1) });
  }
  return chunks;
}

/** Merge multi-chunk QR resolve payloads (same shipment across pages → union units). */
export function mergeQrResolveChunkResponses(responses) {
  const byId = new Map();
  const failed = [];
  let scanMethod = null;

  for (const response of responses || []) {
    const data = response?.data || response || {};
    if (!scanMethod && data.scan_method) scanMethod = data.scan_method;

    for (const row of data.shipments || []) {
      const sid = row?.shipment_id;
      if (!sid) continue;
      const existing = byId.get(sid);
      if (!existing) {
        byId.set(sid, {
          ...row,
          scanned_unit_nos: Array.isArray(row.scanned_unit_nos)
            ? [...row.scanned_unit_nos]
            : (row.scanned_unit?.unit_no != null ? [row.scanned_unit.unit_no] : []),
        });
        continue;
      }
      const nos = new Set([
        ...(existing.scanned_unit_nos || []),
        ...(Array.isArray(row.scanned_unit_nos) ? row.scanned_unit_nos : []),
        ...(row.scanned_unit?.unit_no != null ? [row.scanned_unit.unit_no] : []),
      ]);
      existing.scanned_unit_nos = [...nos]
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n))
        .sort((a, b) => a - b);
      if (row.scanned_unit) existing.scanned_unit = row.scanned_unit;
      if (row.unit_no != null) existing.unit_no = row.unit_no;
      if (row.legacy_qr) existing.legacy_qr = true;
    }

    for (const item of data.failed || []) {
      failed.push(item);
    }
  }

  const shipments = [...byId.values()];
  return {
    success: true,
    data: {
      shipments,
      failed,
      count: shipments.length,
      scan_method: scanMethod,
    },
  };
}
